import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ExternalLink, Tag, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { formatIDR } from "@/lib/constants";
import { OVERRECEIVED_CATEGORY_LABEL } from "@/lib/i18nLabels";

// 16-OVERRECEIVED-R1: Badge klasifikasi penerimaan di atas 100%. Label default
// "Perlu Klasifikasi" (16-CONSISTENT-BADGE-R1: diseragamkan di seluruh sistem).
// Harus disinkron dengan CATEGORIES di /app/backend/routers/overreceived.py.
const CATEGORIES = [
  { key: "overpayment", label: "Kelebihan Bayar", noteRequired: false },
  { key: "dp_advance", label: "DP/Uang Muka", noteRequired: false },
  { key: "retention", label: "Retensi", noteRequired: false },
  { key: "misallocation", label: "Salah Alokasi", noteRequired: true },
  { key: "historical_legacy", label: "Penerimaan Historis", noteRequired: false },
  { key: "unidentified", label: "Belum Teridentifikasi", noteRequired: true },
  { key: "other", label: "Lainnya", noteRequired: true },
];

export default function OverReceivedBadge({ projectId, overReceived, excessAmount, classification, onClassified, unclassifiedLabel = "Perlu Klasifikasi" }) {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const nav = useNavigate();
  const [cls, setCls] = useState(classification || null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setCls(classification || null);
  }, [classification]);

  if (!overReceived) return null;
  const excess = Number(excessAmount || 0);
  const canClassify = !!user && canPerm(user, "finance_report", "update");

  const badge = cls ? (
    <span className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-sky-50 text-sky-800 border-sky-300"
      title={`${OVERRECEIVED_CATEGORY_LABEL[cls.category] || cls.category_label}${cls.note ? ` · ${cls.note}` : ""}${cls.classified_by_name ? ` · oleh ${cls.classified_by_name}` : ""}`}>
      <Tag size={9} /> {OVERRECEIVED_CATEGORY_LABEL[cls.category] || cls.category_label || cls.category}
    </span>
  ) : (
    <span className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-rose-50 text-rose-700 border-rose-300">
      <AlertTriangle size={9} /> {unclassifiedLabel}{excess > 0 ? ` +${formatIDR(excess)}` : ""}
    </span>
  );

  return (
    <span className="inline-flex ml-1 align-middle" data-testid={`overreceived-badge-${projectId}`}>
      {canClassify ? (
        <button
          type="button"
          data-testid={`overreceived-badge-btn-${projectId}`}
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          title={cls ? "Ubah klasifikasi kelebihan penerimaan" : "Klasifikasikan kelebihan penerimaan"}
          className="hover:opacity-80"
        >
          {badge}
        </button>
      ) : (
        <span title="Klasifikasi hanya oleh Owner/BOD Lv1 atau Admin Keuangan">{badge}</span>
      )}
      {open && (
        <ClassifyDialog
          projectId={projectId}
          excess={excess}
          current={cls}
          onClose={() => setOpen(false)}
          onSaved={(doc) => { setCls(doc); onClassified?.(projectId, doc); setOpen(false); }}
          onOpenSource={() => nav(`/finance-report?tab=invoice-piutang`)}
        />
      )}
    </span>
  );
}

function ClassifyDialog({ projectId, excess, current, onClose, onSaved, onOpenSource }) {
  const [category, setCategory] = useState(current?.category || "");
  const [note, setNote] = useState(current?.note || "");
  const [saving, setSaving] = useState(false);
  const selected = CATEGORIES.find((c) => c.key === category);
  const noteRequired = !!selected?.noteRequired;

  const save = async () => {
    if (!category) { toast.error("Pilih kategori klasifikasi terlebih dahulu."); return; }
    if (noteRequired && note.trim().length < 3) { toast.error("Catatan wajib diisi untuk kategori ini."); return; }
    setSaving(true);
    try {
      const res = await api.put(`/overreceived/classifications/${projectId}`, {
        category,
        note: note.trim() || null,
      });
      toast.success("Klasifikasi tersimpan & tercatat di Audit Log. Angka transaksi tidak berubah.");
      onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan klasifikasi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white border border-slate-300 shadow-xl"
        data-testid="overreceived-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Penerimaan di Atas 100%</div>
            <div className="text-sm font-bold text-[#0B1D3A]">Klasifikasi Kelebihan Penerimaan</div>
          </div>
          <button type="button" onClick={onClose} data-testid="overreceived-close" className="p-1 text-slate-500 hover:text-slate-800">
            <X size={16} />
          </button>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="text-[11px] text-slate-600">
            Penerimaan melebihi tagihan kas sebesar{" "}
            <span className="font-bold text-rose-700 tabular-nums" data-testid="overreceived-excess">{formatIDR(excess)}</span>.
            Klasifikasi ini hanya penanda verifikasi — <b>angka transaksi tidak berubah</b>. Selisih nantinya
            dikelompokkan sebagai Pendapatan Lainnya saat klasifikasi COA.
          </div>
          <button
            type="button"
            data-testid="overreceived-source-link"
            onClick={onOpenSource}
            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-[#FF5A1F] text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-white"
          >
            <ExternalLink size={11} /> Lihat Transaksi Sumber
          </button>
          <div className="space-y-1.5" data-testid="overreceived-categories">
            {CATEGORIES.map((c) => (
              <label key={c.key} className={`flex items-start gap-2 px-2 py-1.5 border cursor-pointer text-[11px] ${category === c.key ? "border-[#FF5A1F] bg-orange-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <input
                  type="radio"
                  name="overreceived-category"
                  data-testid={`overreceived-cat-${c.key}`}
                  checked={category === c.key}
                  onChange={() => setCategory(c.key)}
                  className="mt-0.5"
                />
                <span className="text-slate-800">
                  {c.label}
                  {c.noteRequired && <span className="ml-1 text-rose-600 font-semibold">*catatan wajib</span>}
                </span>
              </label>
            ))}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
              Catatan {noteRequired ? <span className="text-rose-600">(wajib)</span> : <span className="text-slate-400">(opsional)</span>}
            </div>
            <textarea
              data-testid="overreceived-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={1000}
              placeholder="Jelaskan asal kelebihan penerimaan / rencana tindak lanjut..."
              className="w-full border border-slate-300 px-2 py-1.5 text-[11px] focus:outline-none focus:border-[#FF5A1F]"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button type="button" onClick={onClose} data-testid="overreceived-cancel"
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
            Batal
          </button>
          <button type="button" onClick={save} disabled={saving} data-testid="overreceived-save"
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold bg-[#FF5A1F] text-white hover:bg-[#e04e19] disabled:opacity-50">
            {saving ? "Menyimpan..." : (current ? "Perbarui Klasifikasi" : "Simpan Klasifikasi")}
          </button>
        </div>
      </div>
    </div>
  );
}
